import { FC, useCallback, useState } from 'react';

import TierIcon from '@z-components/atoms/TierIcon';
import ProProfileClientVerificationBadge from '@z-components/atoms/ProProfileClientVerificationBadge';
import TierExplanationModal from '@z-components/molecules/TierExplanationModal';
import BadgeExplanationModal from '@z-components/molecules/BadgeExplanationModal';

import styles from './ProProfileClientContent.module.scss';

const TRANSLATIONS = {
  en: {
    availableArea: 'Available Area',
    call: 'Call',
    clientViewBadgeText: 'Verification',
    tier: {
      bronze: 'Bronze',
      diamond: 'Diamond',
      gold: 'Gold',
      'new-user': 'New User',
      platinum: 'Platinum',
      silver: 'Silver',
    },
  },
  ja: {
    availableArea: '対応可能エリア',
    call: '電話する',
    clientViewBadgeText: '認証情報',
    tier: {
      bronze: 'ブロンズ',
      diamond: 'ダイアモンド',
      gold: 'ゴールド',
      'new-user': 'ニューユーザー',
      platinum: 'プラチナ',
      silver: 'シルバー',
    },
  },
};

interface Props {
  language: 'en' | 'ja';
  proData: {
    availablePrefectures?: Array<{
      citiesCount: number;
      id: string;
      name: string;
    }>;
    avatar?: string;
    location?: string;
    myPageUrl?: string;
    phoneNumber?: string;
    tier: 'bronze' | 'diamond' | 'gold' | 'new-user' | 'platinum' | 'silver';
    title: string;
    verifiedBadges?: { governmentId: boolean; mail: boolean; phone: boolean };
  };
}

const ProProfileClientContent: FC<Props> = ({ language = 'ja', proData }) => {
  const {
    availablePrefectures,
    avatar = '/static/images/empty-avatar.svg',
    location,
    tier,
    title,
    verifiedBadges,
  } = proData;
  const { governmentId, mail, phone } = verifiedBadges ?? {};

  const hasVerifiedBadge = governmentId ?? mail ?? phone;

  const [showTierExplantionModal, setShowTierExplantionModal] = useState(false);
  const [showBadgeExplantionModal, setShowBadgeExplantionModal] = useState(false);

  const onHelpIconClick = useCallback(() => {
    setShowBadgeExplantionModal(true);
  }, []);

  const onTierIconClick = useCallback(() => {
    setShowTierExplantionModal(true);
  }, []);

  const handleModalDismiss = useCallback(() => {
    setShowBadgeExplantionModal(false);
    setShowTierExplantionModal(false);
  }, []);

  return (
    <>
      <article className={styles['pro-profile-content']}>
        <div className={styles['avatar-container']}>
          <img className={styles['avatar']} src={avatar} alt={title} />
        </div>
        <div className={styles['details']}>
          <section className={styles['details-header-section']}>
            <h1 className={styles['pro-title']}>{title}</h1>
            <div>
              <div className={styles['button-container']}>
                <button className={styles['tier-icon']} onClick={onTierIconClick}>
                  <TierIcon size={24} type={tier} />
                  {/* @ts-expect-error no correctly typed */}
                  <p>{TRANSLATIONS[language].tier[`${tier}`]}</p>
                </button>
              </div>
            </div>
          </section>
        </div>

        {location && (
          <section className={styles['location-section']}>
            <p>
              <img
                className={styles['location-icon']}
                src="/static/images/map-marker-black.svg"
                alt="location"
              />
              {location}
            </p>
          </section>
        )}

        {availablePrefectures && availablePrefectures.length > 0 && (
          <div className={styles['available-prefectures']}>
            <p className={styles['available-prefectures-text']}>
              {TRANSLATIONS[language].availableArea}
            </p>
            <section className={styles['available-prefectures-contents']}>
              {availablePrefectures.map((prefecture) => (
                <div key={prefecture.id}>
                  {prefecture.name}({prefecture.citiesCount})
                </div>
              ))}
            </section>
          </div>
        )}

        {hasVerifiedBadge && verifiedBadges && (
          <div className={styles['client-view-badges-section']}>
            <div className={styles['client-view-badges-container']}>
              <p className={styles['client-view-badges-text']}>
                {TRANSLATIONS[language].clientViewBadgeText}
              </p>
              <button className={styles['help-icon']} onClick={onHelpIconClick}>
                <img src="/static/images/help.svg" alt="help" />
              </button>
            </div>
            <section className={styles['badges']}>
              <ProProfileClientVerificationBadge
                verifiedBadges={verifiedBadges}
                language={language}
              />
            </section>
          </div>
        )}
      </article>
      {showTierExplantionModal && (
        <TierExplanationModal language={language} handleModalDismiss={handleModalDismiss} />
      )}
      {showBadgeExplantionModal && (
        <BadgeExplanationModal language={language} handleModalDismiss={handleModalDismiss} />
      )}
    </>
  );
};

export default ProProfileClientContent;
export type { Props as ProProfileClientContentProps };
